
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ChartComponent, IAccTextRenderEventArgs, ILoadedEventArgs, FontModel, ColumnModel, IMouseEventArgs } from '@syncfusion/ej2-angular-charts';
import { Subscription } from 'rxjs';
import { CustomerService } from '../../shared/services/customer.service';
import { DataScopesEnum, SubmissionStatusEnum } from '../../shared/enums/index';
import { ISubmittedDevicesChart } from '../../shared/models/index';
import { Utilities } from '../../shared/services/utilities';

@Component({
    selector: 'app-submitted-devices-chart',
    templateUrl: './submitted-devices-chart.component.html',
    styleUrls: ['./submitted-devices-chart.component.scss'],
    standalone: false
})
export class SubmittedDevicesChartComponent implements OnInit, AfterViewInit, OnDestroy {

  dataScopeEnum: any;
  submissionStatusEnum: any;

  // chart init variables
  primaryXAxis: Object;
  primaryYAxis: Object;
  chartData: Object[] = [];
  fullChartData: Object[] = [];
  marker: Object;
  lineMarker: Object;
  enableAnimation = true;
  legendSettings: Object;
  chartTitle: string;
  tooltip: Object;
  titleStyle: Object;
  chartArea: Object;
  cornerRadius: Object;
  pointColorMapping: string;
  maxCountForYaxis = 0;
  border: Object;
  margin: Object;
  emptySeries: Object;
  fontModel: FontModel;
  columnModel: ColumnModel;
  radius: Object;
  chartComplete = false;

  @Input() customer;
  @Input() user: any;

  submittedDevicesData: ISubmittedDevicesChart[];
  subsciption: Subscription

  @ViewChildren(ChartComponent) chartsQuery: QueryList<ChartComponent>;

  //@ViewChild('chart-container-submitted')
  chart: ChartComponent;

  inProcessStatuses: string[] = [];
  successStatuses: string[] = [];
  failStatuses: string[] = [];
  fullChannelData: {};
  seriesBorder: {};
  subTitle: string;
  subtitleStyle: Object;


  constructor (private cdRef: ChangeDetectorRef, private customerService: CustomerService) {
    // no action is required in the constructor
  }
  ngOnDestroy(): void {
    if (this.subsciption) {
      this.subsciption.unsubscribe();
    }
  }
  ngAfterViewInit(): void {

    this.chart = this.chartsQuery.last;
    if (this.chart) {
      this.chart.background = '#fff';
    }

    this.cdRef.detectChanges();
  }

  ngOnInit(): void {

    this.dataScopeEnum = DataScopesEnum;
    this.submissionStatusEnum = SubmissionStatusEnum;

    this.prepareDataForChart();
  }

  prepareDataForChart() {

    if (this.customer) {
      this.subsciption = this.customerService.getCustomerSubmittedDevicesChartData({ 'clientId': this.customer.customerId, 'userGuid': this.user.userguid }).subscribe(data => {
        this.submittedDevicesData = data;

        this.inProcessStatuses = [
          this.submissionStatusEnum.readyForApproval.toLowerCase(),
          this.submissionStatusEnum.submissionApproved.toLowerCase(),
          this.submissionStatusEnum.submissionInProcess.toLowerCase(),
          this.submissionStatusEnum.pendingModelRegistration.toLowerCase(),
          this.submissionStatusEnum.submissionRejected.toLowerCase()
        ]

        this.successStatuses = [
           this.submissionStatusEnum.submissionPassed.toLowerCase(),
         ]

        this.failStatuses = [
          this.submissionStatusEnum.submissionFailed.toLowerCase(),
          this.submissionStatusEnum.haSystemError.toLowerCase(),
          this.submissionStatusEnum.invalidCredentials.toLowerCase(),
          this.submissionStatusEnum.internalSystemError.toLowerCase(),
          this.submissionStatusEnum.hungRecord.toLowerCase()
        ]

        // sort array in descending order to get the highest validation count for the yAxis
        if (this.submittedDevicesData && this.submittedDevicesData.length) {

          // create a deep independent copy of the original array so it didn't get affected by any action
          let sortedArray: ISubmittedDevicesChart[] = [...this.submittedDevicesData];

          // replace null with 'undefined'
          sortedArray.forEach(element => {
            if (element.submissionStatus === null) {
              element.submissionStatus = 'undefined';
            }
          });

          // Check data for the current customer application variants
          if (this.customer.applicationVariants && this.customer.applicationVariants.length) {

            this.customer.applicationVariants.forEach(channel => {

              let channelData = {};
              // create a deep independent copy
              const channelArray: ISubmittedDevicesChart[] = [...this.submittedDevicesData].filter((x: ISubmittedDevicesChart) => x.channel.toLowerCase() === channel.applicationVariantName.toLowerCase());

              if (channelArray && channelArray.length) {

                // handling null in validation status that breaks the exectution
                channelArray.forEach((element: ISubmittedDevicesChart) => {

                  // standardaize the Channel names
                  switch (element.channel.toLowerCase()) {
                    case 'eudamed':
                      element.channel = this.dataScopeEnum.EUDAMED;
                      break;

                      case 'swissdamed':
                      element.channel = this.dataScopeEnum.SWISSDAMED;
                      break;

                    case 'china-udi':
                      element.channel = this.dataScopeEnum.CHINA;
                      break;

                    case 'southkorea-udi':
                      element.channel = this.dataScopeEnum.SKOREA;
                      break;

                    case 'us-udi':
                      element.channel = this.dataScopeEnum.US;
                      break;

                    case 'saudiarabia-udi':
                      element.channel = this.dataScopeEnum.SA;
                      break;
                    case 'australia-udi':
                      element.channel = this.dataScopeEnum.AU;
                      break;
                    case 'brazil-udi':
                      element.channel = this.dataScopeEnum.BRAZIL;
                      break;
                  }
                });

                channelData = {
                  'channel': channel.applicationVariantName,
                  'inprocess': this.getStatusCount(channelArray, this.inProcessStatuses),
                  'success': this.getStatusCount(channelArray, this.successStatuses),
                  'fail': this.getStatusCount(channelArray, this.failStatuses)
                };

                this.fullChannelData = this.getFullChannelData(channelArray);

              } else {
                channelData = {
                  'channel': channel.applicationVariantName,
                  'inprocess': 0,
                  'success': 0,
                  'fail': 0
                };
              }

              this.chartData.push(channelData);
              this.fullChartData.push(this.fullChannelData);
            });

            // sort descending by submissionStatusCount in order to set the Y Axis to the highest level
            let valuesArray = [];
            let copied: Object = [...this.chartData];

            Object.values(copied).forEach(channel => {

              let numbers = [];
              //Remove the "channel" key from the Object and convert them all to numbers, just in case
              numbers = Object.values(channel).splice(1).map(x => +x);

              // sort Descending
              numbers.sort(Utilities.compareDesc);
              valuesArray.push(numbers[0]);
            });

            // get the higest number for the YAxis
            valuesArray.sort(Utilities.compareDesc);
            this.maxCountForYaxis = valuesArray.length ? valuesArray[0] : 0;
          }
        }

        this.initChart();

        this.chartComplete = true;
      });
    }
  }


  getFullChannelData(channelArray: ISubmittedDevicesChart[]): {} {

    let fullChannelData: any;

    if (channelArray && channelArray.length) {
      fullChannelData = {
        'channel': channelArray[0].channel,
        'readyForApproval': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.readyForApproval.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.readyForApproval.toLowerCase()).submissionStatusCount : 0,
        'submissionApproved': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionApproved.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionApproved.toLowerCase()).submissionStatusCount : 0,
        'submissionRejected': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionRejected.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionRejected.toLowerCase()).submissionStatusCount : 0,
        'submissionInProcess': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionInProcess.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionInProcess.toLowerCase()).submissionStatusCount : 0,
        'submissionFailed': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionFailed.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionFailed.toLowerCase()).submissionStatusCount : 0,
        'pendingModelRegistration': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.pendingModelRegistration.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.pendingModelRegistration.toLowerCase()).submissionStatusCount : 0,
        'replaced': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.replaced.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.replaced.toLowerCase()).submissionStatusCount : 0,
        'haSystemError': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.haSystemError.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.haSystemError.toLowerCase()).submissionStatusCount : 0,
        'invalidCredentials': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.invalidCredentials.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.invalidCredentials.toLowerCase()).submissionStatusCount : 0,
        'internalSystemError': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.internalSystemError.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.internalSystemError.toLowerCase()).submissionStatusCount : 0,
        'submissionPassed': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionPassed.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.submissionPassed.toLowerCase()).submissionStatusCount : 0,
        'hungRecord': channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.hungRecord.toLowerCase()) ? channelArray.find(x => x.submissionStatus.toLowerCase() === this.submissionStatusEnum.hungRecord.toLowerCase()).submissionStatusCount : 0
      }
    } else {
      fullChannelData = {
        'channel': channelArray[0].channel,
        'readyForApproval': 0,
        'submissionApproved': 0,
        'submissionRejected': 0,
        'submissionInProcess': 0,
        'submissionFailed': 0,
        'pendingModelRegistration': 0,
        'replaced': 0,
        'haSystemError': 0,
        'invalidCredentials': 0,
        'internalSystemError': 0,
        'submissionPassed': 0,
        'hungRecord': 0
      };
    }

    return fullChannelData;
  }



  getGroupStatusDS(channel: string, status: string): ISubmittedDevicesChart {

    let retDS: any;
    let channelDS: any;

    channelDS = this.fullChartData.find((ds: ISubmittedDevicesChart) => ds.channel.toLowerCase() === channel.toLowerCase());

    retDS = [{ 'channel': channel, 'submissionStatus': status, 'submissionStatusCount': channelDS[status] }];

    return retDS;
  }

  getStatusCount(channelArray: ISubmittedDevicesChart[], statusesArray: string[]): number {

    let count = 0;

    if (channelArray && channelArray.length && statusesArray && statusesArray.length) {

      statusesArray.forEach(status => {

        if (channelArray.find(x => x.submissionStatus.toLowerCase() === status.toLowerCase())) {

          count += channelArray.find(x => x.submissionStatus.toLowerCase() === status.toLowerCase()).submissionStatusCount;
        }
      });

      return count;
    }
  }

  private initChart() {

    this.radius = { bottomLeft: 0, bottomRight: 0, topLeft: 7, topRight: 7 };

    this.primaryXAxis = {
      valueType: 'Category',
      title: '',
      majorGridLines: { width: 0 },
      majorTickLines: { width: 1 },
      border: { width: 0, color: '#bbb' }
    };

    // Round to the max value plus 10%
    let multiplier = this.maxCountForYaxis * 1.1;
    let percent = 1.1;

    //add 10% so the data label stays above.
    // If this.maxCountForYaxis = 0 set a default maximum to 10 and interval to 2
    const maximumY = this.maxCountForYaxis ? (Math.ceil(this.maxCountForYaxis / multiplier) * multiplier) * percent : 10;
    const intervalY = maximumY ? Math.ceil(maximumY / 7) : 2;

    this.primaryYAxis = {
      title: 'Submitted Devices',
      // titleStyle: {font: { fontWeight: 'bold'}},
      minimum: 0,
      maximum: maximumY,
      interval: intervalY,
      majorGridLines: { width: 1 },
      majorTickLines: { width: 2 }
    };

    this.columnModel = {
      border: { width: 3, color: '#ff0000' }
    }

    this.fontModel = {
      fontWeight: 'regular'
    }

    this.marker = {
      dataLabel: {
        visible: true,
        font: this.fontModel,
        margin: {
          left: 5,
          right: 5,
          top: 1,
          bottom: 1
        }
      }
    };

    this.lineMarker = {
      dataLabel: {
        visible: true,
        font: this.fontModel,
        shape: 'Diamond',
        margin: {
          left: 5,
          right: 5,
          top: 1,
          bottom: 1
        }
      }
    };

    this.emptySeries = {
      mode: 'Drop'
    }

    this.legendSettings = {
      visible: true,
      position: 'Top',
      shapeHeight: 15,
      shapeWidth: 15,
      // textWrap: 'Wrap',
      // maximumLabelWidth: 80,
      // toggleVisibility: false
    };

    this.tooltip = { enable: true };

    this.titleStyle = {
      fontFamily: 'open_sansregular, sans-serif',
      color: '#0071bb',
      size: '18px',
      textWrap: 'Wrap',

    };

    this.subtitleStyle = {
      fontFamily: 'open_sansregular, sans-serif',
      color: '#0071bb',
      size: '14px',
      textWrap: 'AnyWhere'
    };

    this.chartTitle = `Device Submissions by Health Authority`;
    this.subTitle = ` ${ this.customer.customerName } `;

    this.chartArea = {
      border: {
        width: 0,
        color: '#aaa',
        fontWeight: this.fontModel
      }
    };
    this.cornerRadius = {
      topLeft: 5, topRight: 5
    };


    this.pointColorMapping = 'color';
    this.border = { width: 1, color: '#0071bb' }; // [border]='border'
    this.margin = { left: 20, right: 20, top: 20, bottom: 20 }; // [margin]='margin'

    this.seriesBorder = { width: 1, color: '#ff0000' }
  }

  public load(args: ILoadedEventArgs): void {

    //
  }

  onTextRender(args: IAccTextRenderEventArgs): void {
    args.text = `${ args.point.y }`;

    // to prevent displaying 0
    if (args.text === '0') {
      args.cancel = args.point.y === 0;
    }
  }

  public chartMouseClick(args: IMouseEventArgs): void {

    if (((args.target).indexOf('chart_legend_text') > -1) || ((args.target).indexOf('chart_legend_shape') > -1) ||
      (args.target).indexOf('chart_legend_shape_marker_') && !(args.target).indexOf('chart_legend_element')) {
      const status = ((args.target).indexOf('chart_legend_text') > -1) ?
        (args.target).split('chart_legend_text_')[1] : args.target.split('chart_legend_shape_marker_')[1] || args.target.split('chart_legend_shape_')[1];
    };
  }

  consoleIt(something: any) {
    console.log(something);
  }
}

